"use client";

import React from "react";
import { Check, MessageCircle, FileText, Truck, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
const WHATSAPP_NUMBER = "221781666638";
function buildWhatsAppLink(message: string) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}
type BusinessMessageOptions = {
  intro: string;
  includeMachineQuestion?: boolean;
  includeDeliveryQuestion?: boolean;
  includeFrequencyQuestion?: boolean;
  includeNeedType?: boolean;
};
function buildBusinessMessage({
  intro,
  includeMachineQuestion = true,
  includeDeliveryQuestion = true,
  includeFrequencyQuestion = true,
  includeNeedType = false
}: BusinessMessageOptions) {
  const sections = ["Bonjour KBC Coffee Service ", "", intro, "", "Nom de l’entreprise :", "Ville : (Thiès / Dakar / Petite Côte)", "Nombre d’employés :", "Consommation estimée :"];
  if (includeFrequencyQuestion) {
    sections.push("Fréquence souhaitée : hebdomadaire / mensuelle");
  }
  if (includeNeedType) {
    sections.push("Besoin :");
    sections.push("- Capsules");
    sections.push("- Machine");
    sections.push("- Offre entreprise");
  }
  if (includeMachineQuestion) {
    sections.push("Besoin en machine : Oui / Non");
  }
  if (includeDeliveryQuestion) {
    sections.push("Besoin de livraison : Oui / Non");
  }
  sections.push("");
  sections.push("Pouvez-vous me proposer une offre adaptée ?");
  sections.push("");
  sections.push("Merci.");
  return sections.join("\n");
}
const whatsappLinks = {
  starter: buildWhatsAppLink(buildBusinessMessage({
    intro: "Je souhaite un devis pour le Pack Starter."
  })),
  business: buildWhatsAppLink(buildBusinessMessage({
    intro: "Je suis intéressé par le Pack Business."
  })),
  premium: buildWhatsAppLink(buildBusinessMessage({
    intro: "Je souhaite une offre Premium pour mon entreprise."
  })),
  general: buildWhatsAppLink(buildBusinessMessage({
    intro: "Je souhaite une offre entreprise adaptée à mes besoins.",
    includeFrequencyQuestion: false,
    includeNeedType: true
  }))
};
const plans = [{
  name: "Pack Starter",
  description: "Idéal pour petites équipes avec consommation régulière",
  price: "À partir de 30 000 CFA / mois",
  machineImages: ["https://www.kbccoffeeservice.com/wp-content/uploads/2026/03/Machine-Dolce-gusto-picolo-Xs-Rouge.jpg"],
  features: ["6 à 8 boîtes / mois", "Compatible Dolce Gusto & Tassimo", "Machine incluse selon engagement : Dolce Gusto Piccolo XS", "Livraison gratuite à Thiès", "Dakar : offerte dès volume", "Commande simple via WhatsApp"],
  cta: "MACHINE MISE À DISPOSITION",
  note: "Machine Piccolo XS disponible en option pour démarrer facilement",
  buttonText: "Demander un devis",
  buttonHref: whatsappLinks.starter,
  buttonVariant: "outline" as const,
  buttonClass: "w-full border-chart-1 text-chart-1 hover:bg-chart-1 hover:text-primary-foreground transition-all duration-300 font-sans",
  cardClass: "border-border border-t-4 border-t-chart-1 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg bg-card",
  isPremium: false
}, {
  name: "Pack Business",
  description: "Pour entreprises avec consommation régulière et équipes de 5 à 15 personnes",
  price: "À partir de 50 000 CFA / mois",
  highlight: "Le meilleur équilibre prix / confort",
  machineImages: ["https://www.kbccoffeeservice.com/wp-content/uploads/2026/03/Machine-Dolce-gusto-Mini-Me-noir-2.jpg", "https://www.kbccoffeeservice.com/wp-content/uploads/2026/03/bosch-machine-a-cafe-tassimo-happy-3-packs-de-t-discs-119946146.png"],
  features: ["10 à 12 boîtes / mois", "Machines Premium : Nespresso Inissia ou Dolce Gusto Genio S", "Remise sur volume", "Livraison organisée Thiès & Dakar", "Réapprovisionnement simple"],
  cta: "CHOIX MACHINE",
  note: "Le plus choisi par nos clients professionnels",
  buttonText: "Recevoir une offre",
  buttonHref: whatsappLinks.business,
  buttonVariant: "default" as const,
  buttonClass: "w-full bg-chart-1 text-primary-foreground hover:bg-chart-2 transition-all duration-300 font-sans",
  cardClass: "border-border border-t-4 border-t-chart-2 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg md:scale-105 z-10 relative bg-card",
  isPremium: true
}, {
  name: "Pack Premium",
  description: "Solution café haut de gamme pour entreprises à forte consommation",
  price: "Sur devis",
  highlight: "Machines Premium incluses",
  machineImages: ["https://www.kbccoffeeservice.com/wp-content/uploads/2026/05/Machine_1.jpg", "https://www.kbccoffeeservice.com/wp-content/uploads/2026/05/Machine_DG_1.jpg"],
  features: ["15+ boîtes / mois", "Machines Premium : Nespresso Inissia / Dolce Gusto Genio S", "Machine incluse selon engagement", "Livraison incluse", "Suivi personnalisé", "Priorité sur les stocks"],
  cta: "CHOIX MACHINE",
  note: "Solution café premium clé en main pour bureaux et directions",
  buttonText: "Discuter sur WhatsApp",
  buttonHref: whatsappLinks.premium,
  buttonVariant: "default" as const,
  buttonClass: "w-full bg-chart-1 text-primary-foreground hover:bg-chart-2 transition-all duration-300 font-sans",
  cardClass: "border-border border-t-4 border-t-chart-1 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg bg-card",
  isPremium: false
}];
const steps = [{
  icon: MessageCircle,
  title: "Contactez-nous sur WhatsApp",
  description: "Expliquez-nous vos besoins, votre ville et votre consommation estimée."
}, {
  icon: FileText,
  title: "Nous vous proposons une offre adaptée",
  description: "Nous vous orientons vers la formule la plus simple et la plus rentable pour votre entreprise."
}, {
  icon: Truck,
  title: "Livraison depuis Thiès",
  description: "Nous organisons la livraison à Thiès, Dakar et sur la Petite Côte selon votre commande."
}];
const whyUs = ["Livraison régulière à Thiès & Dakar", "Service apprécié par professionnels et particuliers", "Réponse rapide sur WhatsApp", "Capsules compatibles premium", "Machines adaptées selon votre consommation", "Solutions simples pour bureaux et entreprises"];
export default function SubscriptionPlans() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="nos-solutions-pour-entreprises" className="bg-secondary/30 py-[120px] overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 max-w-6xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-chart-2/10 mb-6">
            <Coffee className="w-6 h-6 text-chart-2" />
          </div>

          <h2 className="font-serif text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            Solution Café pour Entreprises à Thiès & Dakar
          </h2>

          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-sans leading-relaxed">
            Des offres simples, adaptées à votre consommation pour garantir une
            pause café sans rupture dans vos bureaux.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to={whatsappLinks.general} newTab>
              <Button size="lg" className="bg-chart-1 text-primary-foreground hover:bg-chart-2 transition-all duration-300 font-sans px-8">
                Contacter sur WhatsApp
              </Button>
            </Link>

            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-muted-foreground font-sans flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                Réponse rapide en quelques minutes
              </p>

              <div className="flex flex-wrap justify-center gap-2 text-xs md:text-sm">
                <span className="rounded-full bg-chart-2/10 px-3 py-1 text-chart-1 font-medium">
                  Livraison Thiès & Dakar
                </span>

                <span className="rounded-full bg-chart-2/10 px-3 py-1 text-chart-1 font-medium">
                  Machines selon vos besoins
                </span>

                <span className="rounded-full bg-chart-2/10 px-3 py-1 text-chart-1 font-medium">
                  Capsules premium compatibles
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch mb-24">
          {plans.map((plan, index) => <div key={index} className="h-full flex">
              <Card className={`w-full flex flex-col py-0 overflow-hidden ${plan.cardClass}`}>
                {plan.isPremium && <div className="absolute top-0 right-0 bg-chart-2 text-primary-foreground px-4 py-1 rounded-bl-xl text-[10px] font-bold tracking-widest uppercase">
                    Plus populaire
                  </div>}

                <CardHeader className="text-center pb-6 pt-12">
                  <CardTitle className="font-serif text-3xl text-foreground mb-3">
                    {plan.name}
                  </CardTitle>

                  <CardDescription className="font-sans text-muted-foreground text-base min-h-[48px] px-4">
                    {plan.description}
                  </CardDescription>

                  <div className="mt-4 text-xl font-bold text-chart-1">
                    {plan.price}
                  </div>

                  {"highlight" in plan && plan.highlight && <div className="mt-4 inline-block rounded-lg bg-chart-2/10 text-chart-2 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider">
                      {plan.highlight}
                    </div>}
                </CardHeader>

                <CardContent className="flex-grow pt-4">
                  <div className="mb-6 text-center">
                    <div className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
                      Machine mise à disposition
                    </div>

                    <div className="font-bold text-chart-1 mb-4">
                      {plan.name === "Pack Starter" && "Dolce Gusto Piccolo XS"}

                      {plan.name === "Pack Business" && "Mini Me / Bosch Tassimo"}

                      {plan.name === "Pack Premium" && "Nespresso Inissia / Genio S"}
                    </div>

                    <div className="flex justify-center gap-3 flex-wrap">
                      {plan.machineImages.map((image, imgIndex) => <img key={imgIndex} src={image} alt={`Machine ${imgIndex + 1} pour ${plan.name}`} className="h-24 md:h-28 w-auto object-contain transition-transform duration-300 hover:scale-105" loading="lazy" />)}
                    </div>
                  </div>

                  <ul className="space-y-4">
                    {plan.features.map((feature, fIndex) => <li key={fIndex} className="flex items-start gap-3">
                        <div className="mt-1 bg-chart-2/10 rounded-lg p-0.5">
                          <Check className="w-4 h-4 text-chart-2 shrink-0" />
                        </div>

                        <span className="font-sans text-foreground/80 leading-snug text-sm md:text-base">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <div className="mt-6 rounded-xl bg-chart-2/10 border border-chart-2/20 p-4 text-center">
                    <p className="text-sm font-semibold text-chart-1">
                      {plan.cta}
                    </p>

                    <p className="text-xs text-muted-foreground mt-1">
                      Installation rapide • Accompagnement personnalisé
                    </p>
                  </div>

                  <div className="mt-8 p-4 rounded-lg bg-muted/30 border border-border/50 text-xs text-muted-foreground font-sans italic text-center">
                    {plan.note}
                  </div>
                </CardContent>

                <CardFooter className="pt-6 pb-10 flex-col items-stretch gap-4">
                  <Link to={plan.buttonHref} newTab className="w-full">
                    <Button variant={plan.buttonVariant} size="lg" className={plan.buttonClass}>
                      {plan.buttonText}
                    </Button>
                  </Link>

                  {plan.isPremium && <p className="text-[11px] text-center text-muted-foreground font-sans uppercase tracking-tight opacity-70">
                      Sans engagement immédiat • Offre ajustée selon vos besoins
                    </p>}
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}